import { useEffect, useState } from 'react';

// Exports
/**
 * Let's you delay scrolling to the HTML element matched by the `scrollToElemSelector` until all
 * registered components have finished loading. Useful when you want to scroll some UI down to the
 * bottom, but you want to wait until all the content has loaded. In a perfect world, this would be
 * handled by a combination of Apollo suspended queries and React <Suspense>, but we don't have
 * suspended queries at the moment.
 *
 * - Components tell the hook to wait on them by calling `onMount`
 * - Components tell the hook that they are ready to go by calling `onReady`
 */
type UseScrollToAfterAllLoaded = {
  scrollToElemSelector: string;
  scrollTimeout?: number;
  behavior?: 'auto' | 'instant' | 'smooth';
};
export const useScrollToAfterAllLoaded = ({
  scrollToElemSelector,
  scrollTimeout = 100,
  behavior = 'smooth',
}: UseScrollToAfterAllLoaded) => {
  const [alreadyScrolled, setAlreadyScrolled] = useState(false);
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (alreadyScrolled) return undefined;
    if (Object.values(loading).some(Boolean)) return undefined;

    const timeoutId = setTimeout(
      () => {
        const scrollToElem = document.querySelector(scrollToElemSelector);
        scrollToElem?.scrollIntoView({ behavior });
        setAlreadyScrolled(true);
      },
      // The timeout here allows a bit of time for components to mount and register
      scrollTimeout,
    );
    return () => clearTimeout(timeoutId);
  }, [alreadyScrolled, scrollToElemSelector, loading, scrollTimeout, behavior]);

  return {
    onMount: (id: string) => setLoading((prev) => ({ ...prev, [id]: true })),
    onReady: (id: string) => setLoading((prev) => ({ ...prev, [id]: false })),
  };
};
